import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { cleanPhone } from "../lib/utils";
import useIsMobile from "../hooks/useIsMobile";

const DEFAULT_STAGE_CONFIG = [
  { id: "new_lead",    label: "New Lead",    color: "#64748b", order: 0, isWon: false, isLost: false },
  { id: "interested",  label: "Interested",  color: "#38bdf8", order: 1, isWon: false, isLost: false },
  { id: "follow_up",   label: "Follow Up",   color: "#f59e0b", order: 2, isWon: false, isLost: false },
  { id: "proposal",    label: "Proposal",    color: "#8b5cf6", order: 3, isWon: false, isLost: false },
  { id: "negotiating", label: "Negotiating", color: "#f97316", order: 4, isWon: false, isLost: false },
  { id: "my_client",   label: "My Client",   color: "#6366f1", order: 5, isWon: true,  isLost: false },
  { id: "lost",        label: "Lost",        color: "#f43f5e", order: 6, isWon: false, isLost: true  },
];
const HANDOFF_STAGE_ID = "__human_handoff__";
const HANDOFF_STAGE = { id: HANDOFF_STAGE_ID, label: "Human Handoff", color: "#f59e0b", isHandoff: true };

// ── Avatar ─────────────────────────────────────────────────────────────────
const Avatar = ({ name, size = 38, color = T.emerald }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.28,
    background: `linear-gradient(135deg,${color}22,${color}11)`,
    border: `1px solid ${color}33`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.38, fontWeight: 700, color, flexShrink: 0,
  }}>
    {(name || "?")[0].toUpperCase()}
  </div>
);

// ── StatCard ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color, sub }) => (
  <div style={{
    background: T.bg1, border: `1px solid ${T.border1}`,
    borderRadius: 14, padding: "14px 18px", minWidth: 120, flex: 1,
  }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: "-0.03em" }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: T.text3, marginTop: 1 }}>{sub}</div>}
  </div>
);

// ── ProfileRow ─────────────────────────────────────────────────────────────
const ProfileRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.text1, lineHeight: 1.5 }}>{value}</div>
    </div>
  );
};

// ── AIPipelineGeneratorModal ───────────────────────────────────────────────
function AIPipelineGeneratorModal({ token, activePipelineId, onCreated, onClose, showToast }) {
  const [step,        setStep]        = useState("prompt"); // "prompt" | "preview"
  const [description, setDescription] = useState("");
  const [generating,  setGenerating]  = useState(false);
  const [pipelines,   setPipelines]   = useState([]);      // generated pipelines
  const [selected,    setSelected]    = useState(0);       // which pipeline tab is active
  const [editStages,  setEditStages]  = useState([]);      // editable copy of selected pipeline stages
  const [applying,    setApplying]    = useState(false);

  const EXAMPLES = [
    "Travel agency specializing in Umrah, Hajj and tour packages",
    "Real estate agency — apartments, villas, commercial plots",
    "Online course institute — leads from Facebook ads",
    "Dental clinic — patient inquiry to confirmed appointment",
  ];

  const generate = async () => {
    if (description.trim().length < 10) {
      showToast?.("Please describe your business in a bit more detail", "error");
      return;
    }
    setGenerating(true);
    try {
      const data = await apiFetch("/api/pipeline/generate", {
        method: "POST",
        body: JSON.stringify({ description: description.trim() }),
      }, token);
      if (data.error) { showToast?.(data.error, "error"); setGenerating(false); return; }
      setPipelines(data.pipelines);
      setSelected(0);
      setEditStages(data.pipelines[0].stages.map(s => ({ ...s })));
      setStep("preview");
    } catch { showToast?.("Generation failed — please try again", "error"); }
    setGenerating(false);
  };

  const switchPipeline = (idx) => {
    setSelected(idx);
    setEditStages(pipelines[idx].stages.map(s => ({ ...s })));
  };

  const updateStage = (idx, key, val) =>
    setEditStages(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));

  const apply = async (createAll = false) => {
    setApplying(true);
    try {
      const toCreate = createAll ? pipelines : [{ ...pipelines[selected], stages: editStages }];
      let firstId = null;
      for (const pl of toCreate) {
        const stages = pl === pipelines[selected] && !createAll ? editStages : pl.stages;
        const data = await apiFetch("/api/pipelines", {
          method: "POST",
          body: JSON.stringify({ name: pl.name, stages }),
        }, token);
        if (data.error) { showToast?.(data.error, "error"); continue; }
        if (!firstId) firstId = data.pipeline._id;
      }
      showToast?.(`${toCreate.length} pipeline${toCreate.length > 1 ? "s" : ""} created`);
      onCreated(firstId);
      onClose();
    } catch { showToast?.("Failed to create pipeline", "error"); }
    setApplying(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: T.bg1, border: `1px solid ${T.border1}`,
        borderRadius: 22, width: "100%", maxWidth: 600, maxHeight: "92vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 28px 90px rgba(0,0,0,0.7)",
      }}>

        {/* Header */}
        <div style={{ padding: "22px 26px 16px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>✨</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.text1 }}>AI Pipeline Generator</div>
            </div>
            <div style={{ fontSize: 12, color: T.text3 }}>
              {step === "prompt" ? "Describe your business — AI will generate pipeline stages automatically" : "Pipeline preview — you can edit stages before applying"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.text3, cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4, marginTop: -2 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 26px" }}>

          {step === "prompt" && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                  Describe your business
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Example: I run a travel agency handling Umrah, Hajj and tour packages. I want separate lead tracking for each service..."
                  rows={5}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "13px 14px", background: T.bg0,
                    border: `1px solid ${T.border2}`, borderRadius: 12,
                    color: T.text1, fontSize: 13, outline: "none",
                    fontFamily: base.font, resize: "vertical", lineHeight: 1.6,
                  }}
                  onFocus={e => (e.target.style.borderColor = "#6366f1")}
                  onBlur={e  => (e.target.style.borderColor = T.border2)}
                />
                <div style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>
                  The more detail you provide, the better the generated pipeline
                </div>
              </div>

              {/* Example chips */}
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 8 }}>Examples — click to use:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {EXAMPLES.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => setDescription(ex)}
                      style={{
                        padding: "6px 12px", borderRadius: 20, border: `1px solid ${T.border2}`,
                        background: T.bg0, color: T.text2, fontSize: 11, cursor: "pointer",
                        fontFamily: base.font, transition: "all 0.15s",
                      }}
                      onMouseOver={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#818cf8"; }}
                      onMouseOut={e  => { e.currentTarget.style.borderColor = T.border2;  e.currentTarget.style.color = T.text2; }}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === "preview" && pipelines.length > 0 && (
            <>
              {/* Pipeline tabs — if multiple generated */}
              {pipelines.length > 1 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
                  {pipelines.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => switchPipeline(i)}
                      style={{
                        padding: "7px 14px", borderRadius: 10, border: "none", cursor: "pointer",
                        fontFamily: base.font, fontSize: 12, fontWeight: 600, transition: "all 0.15s",
                        background: selected === i ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : T.bg0,
                        color:      selected === i ? "#fff" : T.text3,
                        boxShadow:  selected === i ? "0 3px 12px rgba(99,102,241,0.3)" : "none",
                      }}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Selected pipeline name */}
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 14 }}>
                {pipelines[selected].name}
                <span style={{ fontSize: 11, fontWeight: 400, color: T.text3, marginLeft: 8 }}>— stages edit kar sakte ho</span>
              </div>

              {/* Editable stages */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {editStages.map((stage, idx) => (
                  <div key={stage.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", background: T.bg0,
                    border: `1px solid ${T.border1}`, borderLeft: `3px solid ${stage.color}`,
                    borderRadius: 11,
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.text3, minWidth: 20, textAlign: "center" }}>{idx + 1}</span>
                    <input type="color" value={stage.color} onChange={e => updateStage(idx, "color", e.target.value)}
                      style={{ width: 26, height: 26, border: "none", borderRadius: 6, cursor: "pointer", padding: 0, background: "none", flexShrink: 0 }} />
                    <input
                      value={stage.label}
                      onChange={e => updateStage(idx, "label", e.target.value)}
                      style={{
                        flex: 1, padding: "6px 10px", background: T.bg1,
                        border: `1px solid ${T.border1}`, borderRadius: 8,
                        color: T.text1, fontSize: 12, outline: "none", fontFamily: base.font,
                      }}
                      onFocus={e => (e.target.style.borderColor = stage.color)}
                      onBlur={e  => (e.target.style.borderColor = T.border1)}
                    />
                    {stage.isWon  && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: "rgba(16,185,129,0.15)", color: "#10b981", fontWeight: 700, flexShrink: 0 }}>Won</span>}
                    {stage.isLost && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: "rgba(244,63,94,0.15)",  color: "#f43f5e",  fontWeight: 700, flexShrink: 0 }}>Lost</span>}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)", fontSize: 12, color: T.text3 }}>
                Apply karne k baad bhi "⚙ Customize" se kuch bhi change kar sakte ho
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 26px", borderTop: `1px solid ${T.border1}`, flexShrink: 0, display: "flex", gap: 10 }}>
          {step === "preview" && (
            <button
              onClick={() => setStep("prompt")}
              style={{
                padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                background: T.bg0, color: T.text2, border: `1px solid ${T.border1}`,
                fontSize: 13, fontWeight: 600, fontFamily: base.font,
              }}
            >
              ← Back
            </button>
          )}
          <button onClick={onClose} style={{
            flex: step === "preview" ? 0 : 1, padding: "10px 16px", borderRadius: 10, cursor: "pointer",
            background: T.bg0, color: T.text2, border: `1px solid ${T.border1}`,
            fontSize: 13, fontWeight: 600, fontFamily: base.font,
          }}>Cancel</button>

          {step === "prompt" && (
            <button
              onClick={generate}
              disabled={generating || description.trim().length < 10}
              style={{
                flex: 2, padding: "10px", borderRadius: 10, border: "none",
                cursor: generating || description.trim().length < 10 ? "not-allowed" : "pointer",
                background: generating || description.trim().length < 10
                  ? T.bg2
                  : "linear-gradient(135deg,#6366f1,#8b5cf6)",
                color: generating || description.trim().length < 10 ? T.text3 : "#fff",
                fontSize: 13, fontWeight: 700, fontFamily: base.font,
                boxShadow: generating || description.trim().length < 10 ? "none" : "0 4px 16px rgba(99,102,241,0.35)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {generating ? (
                <>
                  <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.25)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  AI Pipeline bana raha hai...
                </>
              ) : "✨ Generate Pipeline"}
            </button>
          )}

          {step === "preview" && (
            <>
              {pipelines.length > 1 && (
                <button
                  onClick={() => apply(true)}
                  disabled={applying}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 10,
                    cursor: applying ? "not-allowed" : "pointer",
                    background: applying ? T.bg2 : "rgba(99,102,241,0.15)",
                    color: applying ? T.text3 : "#818cf8",
                    fontSize: 12, fontWeight: 700, fontFamily: base.font, border: "1px solid rgba(99,102,241,0.3)",
                  }}
                >
                  Create All ({pipelines.length})
                </button>
              )}
              <button
                onClick={() => apply(false)}
                disabled={applying}
                style={{
                  flex: 2, padding: "10px", borderRadius: 10, border: "none",
                  cursor: applying ? "not-allowed" : "pointer",
                  background: applying ? T.bg2 : "linear-gradient(135deg,#10b981,#059669)",
                  color: applying ? T.text3 : "#fff",
                  fontSize: 13, fontWeight: 700, fontFamily: base.font,
                  boxShadow: applying ? "none" : "0 4px 16px rgba(16,185,129,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {applying ? (
                  <>
                    <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.25)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    Creating...
                  </>
                ) : `✓ Create "${pipelines[selected]?.name}"`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PipelineSettingsModal ──────────────────────────────────────────────────
function PipelineSettingsModal({ stageConfig, activePipelineId, token, onSave, onClose, showToast }) {
  const [local,          setLocal]          = useState(() => stageConfig.map((s, i) => ({ ...s, order: i })));
  const [followSettings, setFollowSettings] = useState({ followUpsEnabled: false, aiFollowUpsEnabled: false, followUps: [] });
  const [activeTab,      setActiveTab]      = useState("stages");
  const [saving,         setSaving]         = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch("/api/pipeline/followup-settings", {}, token)
      .then(d => {
        if (!alive) return;
        setFollowSettings({
          followUpsEnabled: d.followUpsEnabled || false,
          aiFollowUpsEnabled: d.aiFollowUpsEnabled || false,
          followUps: (d.followUps || []).map(f => ({
            ...f,
            scheduledAt: f.scheduledAt || new Date(Date.now() + (Number(f.delayMinutes) || 30) * 60000).toISOString(),
          })),
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);

  const update = (idx, key, val) =>
    setLocal(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));

  const markWon = (idx) =>
    setLocal(prev => prev.map((s, i) => ({
      ...s,
      isWon:  i === idx ? !s.isWon : s.isWon,
      isLost: i === idx && !s.isWon ? false : s.isLost,
    })));

  const markLost = (idx) =>
    setLocal(prev => prev.map((s, i) => ({
      ...s,
      isLost: i === idx ? !s.isLost : s.isLost,
      isWon:  i === idx && !s.isLost ? false : s.isWon,
    })));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setLocal(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const moveDown = (idx) => {
    if (idx === local.length - 1) return;
    setLocal(prev => {
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const addStage = () => {
    setLocal(prev => [...prev, {
      id: `stage_${Date.now()}`, label: "New Stage",
      color: "#64748b", order: prev.length, isWon: false, isLost: false,
    }]);
  };

  const deleteStage = (idx) => {
    if (local.length <= 2) return;
    setLocal(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };

  const updateFollowUp = (idx, field, val) => {
    setFollowSettings(prev => {
      const followUps = [...(prev.followUps || [])];
      followUps[idx] = { ...followUps[idx], [field]: val };
      return { ...prev, followUps };
    });
  };

  const addFollowUp = () => {
    const scheduledAt = new Date(Date.now() + 30 * 60000).toISOString();
    setFollowSettings(prev => ({
      ...prev,
      followUps: [
        ...(prev.followUps || []),
        { index: (prev.followUps || []).length, delayMinutes: 30, scheduledAt, message: "" },
      ],
    }));
  };

  const removeFollowUp = (idx) => {
    setFollowSettings(prev => ({
      ...prev,
      followUps: (prev.followUps || []).filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (local.some(s => !s.label.trim())) {
      showToast?.("All stages must have a label", "error");
      return;
    }
    setSaving(true);
    try {
      const stages = local.map((s, i) => ({
        id:     s.id,
        label:  s.label.trim(),
        color:  s.color,
        order:  i,
        isWon:  s.isWon,
        isLost: s.isLost,
      }));
      if (activePipelineId) {
        await apiFetch(`/api/pipelines/${activePipelineId}`, {
          method: "PUT",
          body: JSON.stringify({ stages }),
        }, token);
      } else {
        await apiFetch("/api/pipeline-settings", {
          method: "POST",
          body: JSON.stringify({ stages }),
        }, token);
      }
      await apiFetch("/api/pipeline/followup-settings", {
        method: "POST",
        body: JSON.stringify(followSettings),
      }, token);
      onSave(stages);
      showToast?.("Pipeline settings saved");
      onClose();
    } catch {
      showToast?.("Failed to save settings", "error");
    }
    setSaving(false);
  };

  const inp = {
    width: "100%", background: T.bg0, border: `1px solid ${T.border2}`,
    borderRadius: 8, color: T.text1, padding: "8px 12px",
    fontSize: 13, outline: "none", boxSizing: "border-box",
    fontFamily: base.font,
  };
  const lbl = { fontSize: 12, fontWeight: 600, color: T.text2, marginBottom: 4, display: "block" };
  const tog = (on) => ({
    width: 40, height: 22, borderRadius: 11,
    background: on ? "#6366f1" : T.bg3,
    position: "relative", cursor: "pointer", flexShrink: 0,
    transition: "background 0.2s", border: `1px solid ${on ? "#6366f1" : T.border2}`,
  });
  const knob = (on) => ({
    position: "absolute", top: 2, left: on ? 18 : 2,
    width: 16, height: 16, borderRadius: "50%",
    background: "#fff", transition: "left 0.2s",
  });
  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setActiveTab(id)}
      style={{
        padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
        cursor: "pointer", border: "none",
        background: activeTab === id ? "rgba(99,102,241,0.15)" : "transparent",
        color: activeTab === id ? "#6366f1" : T.text3,
        fontFamily: base.font,
      }}
    >
      {label}
    </button>
  );
  const formatDateTimeLocal = (value) => {
    const date = value ? new Date(value) : new Date(Date.now() + 30 * 60000);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };
  const formatDateLocal = (value) => formatDateTimeLocal(value).slice(0, 10);
  const formatTimeLocal = (value) => formatDateTimeLocal(value).slice(11, 16);
  const combineScheduledDateTime = (currentValue, part, nextValue) => {
    const fallback = formatDateTimeLocal(currentValue);
    const datePart = part === "date" ? nextValue : fallback.slice(0, 10);
    const timePart = part === "time" ? nextValue : fallback.slice(11, 16);
    return toIsoDateTime(`${datePart}T${timePart}`);
  };
  const toIsoDateTime = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
  };
  const schedulerIcon = (type) => (
    <span style={{
      position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
      width: 15, height: 15, color: T.text3, pointerEvents: "none", display: "flex",
    }}>
      {type === "calendar" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      )}
    </span>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: T.bg1, border: `1px solid ${T.border1}`,
        borderRadius: 20, width: "100%", maxWidth: 580, maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,0.65)",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text1 }}>Pipeline Settings</div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Edit stages and follow-up reminders</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.text3, cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ display: "flex", gap: 4, padding: "10px 24px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
          {tabBtn("stages", "Stages")}
          {tabBtn("followups", "Follow-Ups")}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "12px 24px" }}>
          {activeTab === "stages" && (
            <>
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: T.text3, marginBottom: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
                  Won = AI disabled + counts as client
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f43f5e", display: "inline-block" }} />
                  Lost = negative outcome
                </span>
              </div>
              {local.map((stage, idx) => (
                <div key={stage.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 12px", marginBottom: 8,
                  background: T.bg2, border: `1px solid ${T.border1}`,
                  borderRadius: 12, borderLeft: `3px solid ${stage.color}`,
                }}>
                  {/* Reorder arrows */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
                    <button onClick={() => moveUp(idx)} disabled={idx === 0}
                      style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", color: T.text3, opacity: idx === 0 ? 0.15 : 0.55, fontSize: 11, lineHeight: 1, padding: "2px 4px" }}>▲</button>
                    <button onClick={() => moveDown(idx)} disabled={idx === local.length - 1}
                      style={{ background: "none", border: "none", cursor: idx === local.length - 1 ? "default" : "pointer", color: T.text3, opacity: idx === local.length - 1 ? 0.15 : 0.55, fontSize: 11, lineHeight: 1, padding: "2px 4px" }}>▼</button>
                  </div>

                  <input type="color" value={stage.color} onChange={e => update(idx, "color", e.target.value)}
                    title="Pick stage color"
                    style={{ width: 30, height: 30, border: "none", borderRadius: 8, cursor: "pointer", padding: 0, background: "none", flexShrink: 0 }} />

                  <input
                    value={stage.label}
                    onChange={e => update(idx, "label", e.target.value)}
                    placeholder="Stage name"
                    style={{
                      flex: 1, padding: "7px 10px", background: T.bg0, border: `1px solid ${T.border1}`,
                      borderRadius: 8, color: T.text1, fontSize: 12, outline: "none", fontFamily: base.font,
                    }}
                    onFocus={e => (e.target.style.borderColor = stage.color)}
                    onBlur={e  => (e.target.style.borderColor = T.border1)}
                  />

                  <button
                    onClick={() => markWon(idx)}
                    title="Mark as Won stage — disables AI auto-reply"
                    style={{
                      padding: "4px 9px", borderRadius: 8, cursor: "pointer", fontSize: 10, fontWeight: 700, flexShrink: 0,
                      border: `1px solid ${stage.isWon ? "#10b98166" : T.border1}`,
                      background: stage.isWon ? "rgba(16,185,129,0.15)" : "transparent",
                      color: stage.isWon ? "#10b981" : T.text3,
                    }}>Won</button>

                  <button
                    onClick={() => markLost(idx)}
                    title="Mark as Lost stage — negative outcome"
                    style={{
                      padding: "4px 9px", borderRadius: 8, cursor: "pointer", fontSize: 10, fontWeight: 700, flexShrink: 0,
                      border: `1px solid ${stage.isLost ? "#f43f5e66" : T.border1}`,
                      background: stage.isLost ? "rgba(244,63,94,0.15)" : "transparent",
                      color: stage.isLost ? "#f43f5e" : T.text3,
                    }}>Lost</button>

                  <button onClick={() => deleteStage(idx)} disabled={local.length <= 2}
                    title={local.length <= 2 ? "Need at least 2 stages" : "Delete stage"}
                    style={{
                      background: "none", border: "none", cursor: local.length <= 2 ? "default" : "pointer",
                      color: "#f43f5e", opacity: local.length <= 2 ? 0.2 : 0.55,
                      fontSize: 15, lineHeight: 1, padding: 4, flexShrink: 0,
                    }}>🗑</button>
                </div>
              ))}

              <button onClick={addStage} style={{
                width: "100%", padding: "10px", borderRadius: 10, cursor: "pointer",
                background: "transparent", border: `1px dashed ${T.border1}`,
                color: T.text3, fontSize: 12, fontFamily: base.font, marginTop: 4,
                transition: "all 0.15s",
              }}
                onMouseOver={e => { e.currentTarget.style.borderColor = T.emerald; e.currentTarget.style.color = T.emerald; }}
                onMouseOut={e  => { e.currentTarget.style.borderColor = T.border1; e.currentTarget.style.color = T.text3; }}
              >+ Add Stage</button>
            </>
          )}

          {activeTab === "followups" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "14px 16px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Enable AI Follow-Ups</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>AI chooses the timing and writes the message from the chat context</div>
                </div>
                <div style={tog(followSettings.aiFollowUpsEnabled)} onClick={() => setFollowSettings(s => ({ ...s, aiFollowUpsEnabled: !s.aiFollowUpsEnabled }))}>
                  <div style={knob(followSettings.aiFollowUpsEnabled)} />
                </div>
              </div>

              {followSettings.aiFollowUpsEnabled && (
                <div style={{ marginBottom: 16, padding: "11px 14px", borderRadius: 10, background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.18)", color: T.text3, fontSize: 12, lineHeight: 1.5 }}>
                  If a customer says something like "2 din baad message karna", AI will schedule that follow-up automatically. It can also choose a sensible follow-up time for interested leads who stop responding.
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "14px 16px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Manual Follow-Ups</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>Use fixed messages and dates for leads in follow-up stages</div>
                </div>
                <div style={tog(followSettings.followUpsEnabled)} onClick={() => setFollowSettings(s => ({ ...s, followUpsEnabled: !s.followUpsEnabled }))}>
                  <div style={knob(followSettings.followUpsEnabled)} />
                </div>
              </div>

              {followSettings.followUpsEnabled && (followSettings.followUps || []).map((fu, i) => (
                <div key={i} style={{ background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text2 }}>Follow-Up #{i + 1}</div>
                    <button onClick={() => removeFollowUp(i)} style={{ background: "none", border: "none", color: "#f43f5e", cursor: "pointer", fontSize: 12, fontFamily: base.font }}>Remove</button>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ ...lbl, marginBottom: 2 }}>Send Date & Time</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ position: "relative" }}>
                        {schedulerIcon("calendar")}
                        <input
                          type="date"
                          value={formatDateLocal(fu.scheduledAt)}
                          onChange={e => updateFollowUp(i, "scheduledAt", combineScheduledDateTime(fu.scheduledAt, "date", e.target.value))}
                          style={{ ...inp, width: 155, paddingLeft: 34 }}
                        />
                      </div>
                      <div style={{ position: "relative" }}>
                        {schedulerIcon("clock")}
                        <input
                          type="time"
                          value={formatTimeLocal(fu.scheduledAt)}
                          onChange={e => updateFollowUp(i, "scheduledAt", combineScheduledDateTime(fu.scheduledAt, "time", e.target.value))}
                          style={{ ...inp, width: 135, paddingLeft: 34 }}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Message</label>
                    <textarea
                      rows={3}
                      value={fu.message || ""}
                      onChange={e => updateFollowUp(i, "message", e.target.value)}
                      style={{ ...inp, resize: "vertical", fontSize: 12 }}
                    />
                  </div>
                </div>
              ))}

              {followSettings.followUpsEnabled && (
                <button
                  onClick={addFollowUp}
                  style={{ width: "100%", padding: "10px", borderRadius: 9, border: `1px dashed ${T.border1}`, background: "transparent", color: T.text3, cursor: "pointer", fontSize: 12, fontFamily: base.font, marginTop: 4 }}
                >
                  + Add Follow-Up
                </button>
              )}

              {!followSettings.followUpsEnabled && (
                <div style={{ color: T.text3, fontSize: 13, textAlign: "center", padding: 24 }}>
                  Enable manual follow-ups to configure fixed messages.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border1}`, flexShrink: 0, display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "10px", borderRadius: 10, cursor: "pointer",
            background: T.bg2, color: T.text2, border: `1px solid ${T.border1}`,
            fontSize: 13, fontWeight: 600, fontFamily: base.font,
          }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            flex: 2, padding: "10px", borderRadius: 10, cursor: saving ? "default" : "pointer",
            background: saving ? T.bg2 : "linear-gradient(135deg,#6366f1,#4f46e5)",
            color: saving ? T.text3 : "#fff", border: "none",
            fontSize: 13, fontWeight: 700, fontFamily: base.font,
            boxShadow: saving ? "none" : "0 4px 16px rgba(99,102,241,0.3)",
          }}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}

// ── PipelineCard ───────────────────────────────────────────────────────────
function PipelineCard({ card, stage, isSelected, onClick, onDragStart, onDragEnd, isDragging, stageList, onMove, canDrag = true }) {
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const name = card.name || card.phone || cleanPhone(card.contact);
  const latestMessageDateTime = card.lastMessageAt
    ? new Date(card.lastMessageAt).toLocaleString([], {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "";

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={onClick}
      style={{
        background: isSelected ? `${stage.color}10` : T.bg2,
        border: `1px solid ${isSelected ? stage.color + "44" : T.border1}`,
        borderRadius: 10, padding: "10px 11px", marginBottom: 6, cursor: "pointer",
        opacity: isDragging ? 0.4 : 1, transition: "all 0.12s", position: "relative",
      }}
      className="card-hover"
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
        <Avatar name={name} size={32} color={stage.color} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </div>
          <div style={{ fontSize: 10, color: T.text3, marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
            {card.phone && <span>{card.phone}</span>}
            {card.isHumanMode && <span style={{ color: T.amber, fontWeight: 700 }}>· AI OFF</span>}
          </div>
        </div>
        <div
          title={latestMessageDateTime}
          style={{ fontSize: 9, color: T.text3, flexShrink: 0, textAlign: "right", maxWidth: 82 }}
        >
          {latestMessageDateTime}
        </div>
      </div>

      {card.lastMessage && (
        <div style={{ fontSize: 11, color: T.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 6, lineHeight: 1.4 }}>
          {card.lastMessage}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {(card.tags || []).slice(0, 2).map(t => (
            <span key={t} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "rgba(139,92,246,0.12)", color: "#8b5cf6" }}>{t}</span>
          ))}
          {stage.isHandoff && (
            <span style={{ fontSize:9,padding:"1px 6px",borderRadius:8,background:"rgba(245,158,11,0.12)",color:T.amber,fontWeight:700 }}>
              {stageList.find(s=>s.id===card.pipelineStage)?.label || "Lead"}
            </span>
          )}
          {card.unreadCount > 0 && (
            <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "rgba(99,102,241,0.15)", color: T.emerald, fontWeight: 700 }}>
              {card.unreadCount} new
            </span>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button
            onClick={e => { e.stopPropagation(); setShowMoveMenu(m => !m); }}
            style={{ background: "none", border: `1px solid ${T.border1}`, borderRadius: 6, color: T.text3, cursor: "pointer", fontSize: 10, padding: "2px 6px", fontFamily: base.font }}
          >
            Move →
          </button>
          {showMoveMenu && (
            <div onClick={e => e.stopPropagation()} style={{
              position: "absolute", bottom: "100%", right: 0, marginBottom: 4, zIndex: 50,
              background: T.bg1, border: `1px solid ${T.border1}`, borderRadius: 10,
              padding: 6, minWidth: 170, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            }}>
              {stageList.filter(s => s.id !== card.pipelineStage).map(s => (
                <div key={s.id}
                  onClick={() => { onMove(s.id); setShowMoveMenu(false); }}
                  style={{
                    padding: "6px 10px", borderRadius: 7, cursor: "pointer", fontSize: 11,
                    color: s.color, display: "flex", alignItems: "center", gap: 7,
                    transition: "background 0.1s",
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = `${s.color}12`)}
                  onMouseOut={e  => (e.currentTarget.style.background = "transparent")}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, display: "inline-block", flexShrink: 0 }} />
                  {s.label}
                  {s.isWon  && <span style={{ fontSize: 9, opacity: 0.6, marginLeft: "auto" }}>Won</span>}
                  {s.isLost && <span style={{ fontSize: 9, opacity: 0.6, marginLeft: "auto" }}>Lost</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function PipelinePage({ token, showToast, socket, userRole = "owner" }) {
  const [stageConfig,      setStageConfig]      = useState(DEFAULT_STAGE_CONFIG);
  const [stages,           setStages]           = useState({});
  const [stats,            setStats]            = useState({ total: 0, interested: 0, proposals: 0, clients: 0, convRate: 0 });
  const [loading,          setLoading]          = useState(true);
  const [selected,         setSelected]         = useState(null);
  const [chat,             setChat]             = useState([]);
  const [chatLoading,      setChatLoading]      = useState(false);
  const [chatMeta,         setChatMeta]         = useState({ isHumanMode: false });
  const [message,          setMessage]          = useState("");
  const [sending,          setSending]          = useState(false);
  const [panelTab,         setPanelTab]         = useState("chat");
  const [dragOver,         setDragOver]         = useState(null);
  const [dragging,         setDragging]         = useState(null);
  const [search,           setSearch]           = useState("");
  const [moving,           setMoving]           = useState(false);
  const [profile,          setProfile]          = useState(null);
  const [commentText,      setCommentText]      = useState("");
  const [commentSaving,    setCommentSaving]    = useState(false);
  const [deleting,         setDeleting]         = useState(false);
  const [confirmDel,       setConfirmDel]       = useState(false);
  const [settingsOpen,     setSettingsOpen]     = useState(false);
  const [globalAiEnabled,  setGlobalAiEnabled]  = useState(true);
  const [globalAiSaving,   setGlobalAiSaving]   = useState(false);
  const [aiGenOpen,        setAiGenOpen]        = useState(false);
  // ── Multi-pipeline state ──────────────────────────────────────────
  const [pipelines,        setPipelines]        = useState([]);
  const [activePipelineId, setActivePipelineId] = useState(null);
  const [renamingId,       setRenamingId]       = useState(null);
  const [renameVal,        setRenameVal]        = useState("");
  const [deletingPipeline, setDeletingPipeline] = useState(null);
  const [newPipelineModal, setNewPipelineModal] = useState(false);
  const [creatingPipeline, setCreatingPipeline] = useState(false);
  const chatEndRef = useRef(null);
  const canManagePipelines = userRole !== "agent";
  const isTablet = useIsMobile(980);
  const isPhone = useIsMobile(560);

  const stageMap = useMemo(() => Object.fromEntries(stageConfig.map(s => [s.id, s])), [stageConfig]);
  const wonStage = useMemo(() => stageConfig.find(s => s.isWon), [stageConfig]);

  const loadPipelines = useCallback(async () => {
    try {
      const d = await apiFetch("/api/pipelines", {}, token);
      const list = d.pipelines || [];
      setPipelines(list);
      // Set active to default if not set yet
      setActivePipelineId(prev => {
        if (prev && list.find(p => p._id === prev)) return prev;
        return list.find(p => p.isDefault)?._id || list[0]?._id || null;
      });
    } catch {}
  }, [token]);

  const load = useCallback(async (pipelineId) => {
    try {
      const pid = pipelineId || activePipelineId;
      const url = pid ? `/api/pipeline?pipelineId=${pid}` : "/api/pipeline";
      const d = await apiFetch(url, {}, token);
      if (d.stageConfig?.length) setStageConfig(d.stageConfig);
      setStages(d.stages || {});
      setStats(d.stats   || {});
    } catch { showToast?.("Failed to load pipeline", "error"); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activePipelineId]);

  // Initial load
  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  useEffect(() => {
    apiFetch("/api/bot-settings", {}, token)
      .then(d => setGlobalAiEnabled(d.settings?.leadGenAiEnabled !== false))
      .catch(() => {});
  }, [token]);

  const toggleGlobalAi = async () => {
    if (globalAiSaving) return;
    const next = !globalAiEnabled;
    setGlobalAiEnabled(next);
    setGlobalAiSaving(true);
    try {
      await apiFetch("/api/bot-settings", {
        method: "POST",
        body: JSON.stringify({ leadGenAiEnabled: next }),
      }, token);
      showToast?.(next ? "Customer AI enabled for all leads" : "Customer AI disabled for all leads");
    } catch {
      setGlobalAiEnabled(!next);
      showToast?.("Failed to update Customer AI", "error");
    }
    setGlobalAiSaving(false);
  };

  // Reload board when active pipeline changes
  useEffect(() => {
    if (activePipelineId !== null) {
      setLoading(true);
      setSelected(null);
      load(activePipelineId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePipelineId]);

  useEffect(() => {
    if (!socket) return;
    let timer = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => load(activePipelineId), 1200);
    };
    socket.on("pipeline_update", handler);
    socket.on("new_message", handler);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off("pipeline_update", handler);
      socket.off("new_message", handler);
    };
  }, [socket, load, activePipelineId]);

  const deleteContact = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/contacts/${encodeURIComponent(selected.contact)}`, { method: "DELETE" }, token);
      await apiFetch(`/api/chats/${encodeURIComponent(selected.contact)}`, { method: "DELETE" }, token).catch(() => {});
      showToast?.("Contact deleted");
      setSelected(null);
      setConfirmDel(false);
      await load();
    } catch { showToast?.("Failed to delete contact", "error"); }
    setDeleting(false);
  };

  // ── Pipeline management handlers ──────────────────────────────────
  const switchPipeline = (id) => {
    if (id === activePipelineId) return;
    setActivePipelineId(id);
    setSearch("");
  };

  const renamePipeline = async (id) => {
    if (!renameVal.trim()) { setRenamingId(null); return; }
    try {
      await apiFetch(`/api/pipelines/${id}`, { method: "PUT", body: JSON.stringify({ name: renameVal.trim() }) }, token);
      await loadPipelines();
      showToast?.("Pipeline renamed");
    } catch { showToast?.("Rename failed", "error"); }
    setRenamingId(null);
  };

  const deletePipeline = async (id) => {
    try {
      const d = await apiFetch(`/api/pipelines/${id}`, { method: "DELETE" }, token);
      if (d.error) { showToast?.(d.error, "error"); return; }
      showToast?.("Pipeline deleted");
      setDeletingPipeline(null);
      if (activePipelineId === id) setActivePipelineId(null);
      await loadPipelines();
    } catch { showToast?.("Delete failed", "error"); }
  };

  const createManualPipeline = async () => {
    if (creatingPipeline) return;
    setCreatingPipeline(true);
    try {
      const d = await apiFetch("/api/pipelines", {
        method: "POST",
        body: JSON.stringify({ name: "New Pipeline", stages: DEFAULT_STAGE_CONFIG }),
      }, token);
      if (d.error) { showToast?.(d.error, "error"); return; }
      await loadPipelines();
      setActivePipelineId(d.pipeline._id);
      setNewPipelineModal(false);
      setSettingsOpen(true);
      showToast?.("Pipeline created — customize your stages");
    } catch { showToast?.("Failed to create pipeline", "error"); }
    finally { setCreatingPipeline(false); }
  };

  const openContact = async (contact, stage) => {
    setSelected({ contact, stage });
    setConfirmDel(false);
    setPanelTab("chat");
    setChat([]);
    setChatMeta({ isHumanMode: false });
    setProfile(null);
    setCommentText("");
    setChatLoading(true);
    try {
      const [chatData, profileData] = await Promise.all([
        apiFetch(`/api/chats/${encodeURIComponent(contact)}`, {}, token),
        apiFetch(`/api/contacts/${encodeURIComponent(contact)}`, {}, token),
      ]);
      setChat(chatData.messages || []);
      setChatMeta({
        isHumanMode: Boolean(chatData.isHumanMode),
      });
      setProfile(profileData.profile || null);
    } catch { setChat([]); }
    setChatLoading(false);
  };

  useEffect(() => {
    if (panelTab === "chat") chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, panelTab]);

  const sendMsg = async () => {
    if (!message.trim() || !selected || sending) return;
    setSending(true);
    try {
      await apiFetch(`/api/chats/${encodeURIComponent(selected.contact)}/send`, {
        method: "POST",
        body: JSON.stringify({ text: message.trim() }),
      }, token);
      setChat(prev => [...prev, { role: "assistant", content: message.trim(), timestamp: new Date() }]);
      setMessage("");
      setStages(prev => {
        const updated = { ...prev };
        const st = selected.stage;
        if (updated[st]) {
          updated[st] = updated[st].map(c =>
            c.contact === selected.contact
              ? { ...c, lastMessage: message.trim(), lastMessageAt: new Date() }
              : c
          );
        }
        return updated;
      });
    } catch { showToast?.("Failed to send message", "error"); }
    setSending(false);
  };

  const updateAiSettings = async (patch) => {
    if (!selected) return;
    const next = { ...chatMeta, ...patch };
    setChatMeta(next);
    try {
      const d = await apiFetch(`/api/chats/${encodeURIComponent(selected.contact)}/ai-settings`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }, token);
      setChatMeta({
        isHumanMode: Boolean(d.isHumanMode),
      });
      setStages(prev => {
        const updated = { ...prev };
        for (const key of Object.keys(updated)) {
          updated[key] = (updated[key] || []).map(c =>
            c.contact === selected.contact
              ? { ...c, isHumanMode: Boolean(d.isHumanMode) }
              : c
          );
        }
        return updated;
      });
    } catch {
      setChatMeta(chatMeta);
      showToast?.("Failed to update AI setting", "error");
    }
  };

  const addComment = async () => {
    if (!selected || !commentText.trim() || commentSaving) return;
    setCommentSaving(true);
    try {
      const d = await apiFetch(`/api/contacts/${encodeURIComponent(selected.contact)}/comments`, {
        method: "POST",
        body: JSON.stringify({ text: commentText.trim() }),
      }, token);
      setProfile(prev => ({ ...(prev || {}), comments: d.comments || [] }));
      setCommentText("");
    } catch {
      showToast?.("Failed to add comment", "error");
    }
    setCommentSaving(false);
  };

  const moveToStage = async (contact, fromStage, toStage) => {
    if (fromStage === toStage || moving) return;
    setMoving(true);
    try {
      await apiFetch(`/api/pipeline/${encodeURIComponent(contact)}`, {
        method: "PATCH",
        body: JSON.stringify({ stage: toStage, pipelineId: activePipelineId }),
      }, token);

      const targetInfo = stageMap[toStage];
      const isWon = targetInfo?.isWon;

      setStages(prev => {
        const updated = { ...prev };
        const card = (updated[fromStage] || []).find(c => c.contact === contact);
        if (!card) return prev;
        updated[fromStage] = (updated[fromStage] || []).filter(c => c.contact !== contact);
        updated[toStage]   = [
          { ...card, pipelineStage: toStage, isHumanMode: isWon ? true : card.isHumanMode },
          ...(updated[toStage] || []),
        ];
        return updated;
      });

      if (selected?.contact === contact) setSelected(prev => ({ ...prev, stage: toStage }));

      if (isWon) {
        showToast?.(`Moved to ${targetInfo.label} — AI auto-reply disabled`);
      } else {
        showToast?.(`Moved to ${targetInfo?.label || toStage}`);
      }
      await load();
    } catch { showToast?.("Failed to move contact", "error"); }
    setMoving(false);
  };

  const onDragStart = (contact, fromStage) => setDragging({ contact, fromStage });
  const onDragEnd   = () => { setDragging(null); setDragOver(null); };
  const onDragOver  = (e, stageId) => { e.preventDefault(); setDragOver(stageId); };
  const onDrop      = (e, toStage) => {
    e.preventDefault();
    if (dragging && dragging.fromStage !== toStage) moveToStage(dragging.contact, dragging.fromStage, toStage);
    setDragging(null); setDragOver(null);
  };

  const filterCards = (cards) => {
    if (!search) return cards;
    const q = search.toLowerCase();
    return cards.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").includes(q) ||
      (c.lastMessage || "").toLowerCase().includes(q)
    );
  };

  const handoffCards = Object.values(stages).flat().filter(card => card.isHumanMode);
  const boardStages = [HANDOFF_STAGE, ...stageConfig];
  const cardsForStage = stage => stage.isHandoff
    ? handoffCards
    : (stages[stage.id] || []).filter(card => !card.isHumanMode);

  const selectedCard = selected
    ? Object.values(stages).flat().find(c => c.contact === selected.contact)
    : null;

  const activePipeline = pipelines.find(p => p._id === activePipelineId);

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: isTablet ? "column" : "row",
      overflow: isTablet ? "auto" : "hidden",
      background: T.bg0,
      minWidth: 0,
    }}>

      {/* ── Modals ─────────────────────────────────────────────────── */}
      {aiGenOpen && (
        <AIPipelineGeneratorModal
          token={token}
          activePipelineId={activePipelineId}
          onCreated={async (firstId) => {
            await loadPipelines();
            if (firstId) setActivePipelineId(firstId);
          }}
          onClose={() => setAiGenOpen(false)}
          showToast={showToast}
        />
      )}
      {settingsOpen && (
        <PipelineSettingsModal
          stageConfig={stageConfig}
          activePipelineId={activePipelineId}
          token={token}
          onSave={async (newStages) => { setStageConfig(newStages); await load(activePipelineId); }}
          onClose={() => setSettingsOpen(false)}
          showToast={showToast}
        />
      )}
      {/* New pipeline choice modal */}
      {newPipelineModal && (
        <div style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(5px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
          <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:20,padding:"28px 28px 22px",width:"100%",maxWidth:380,boxShadow:"0 24px 70px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize:16,fontWeight:800,color:T.text1,marginBottom:6 }}>New Pipeline</div>
            <div style={{ fontSize:12,color:T.text3,marginBottom:22 }}>How would you like to create it?</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <button disabled={creatingPipeline} onClick={() => { setNewPipelineModal(false); setAiGenOpen(true); }} style={{ padding:"14px 16px",borderRadius:12,border:"1px solid rgba(99,102,241,0.4)",background:"rgba(99,102,241,0.08)",cursor:creatingPipeline?"not-allowed":"pointer",opacity:creatingPipeline?0.55:1,textAlign:"left",fontFamily:base.font }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#a78bfa",marginBottom:2 }}>✨ Generate with AI</div>
                <div style={{ fontSize:11,color:T.text3 }}>Describe your business — AI will generate pipeline stages</div>
              </button>
              <button disabled={creatingPipeline} onClick={createManualPipeline} style={{ padding:"14px 16px",borderRadius:12,border:creatingPipeline?"1px solid rgba(99,102,241,0.45)":`1px solid ${T.border1}`,background:creatingPipeline?"rgba(99,102,241,0.12)":T.bg0,cursor:creatingPipeline?"wait":"pointer",textAlign:"left",fontFamily:base.font }}>
                {creatingPipeline && (
                  <div style={{ display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:T.text1,marginBottom:6 }}>
                    <div style={{ width:14,height:14,border:"2px solid rgba(255,255,255,0.25)",borderTopColor:"#a78bfa",borderRadius:"50%",animation:"spin 0.7s linear infinite",flexShrink:0 }} />
                    Building pipeline...
                  </div>
                )}
                <div style={{ fontSize:13,fontWeight:700,color:T.text1,marginBottom:2 }}>📝 Build Manually</div>
                <div style={{ fontSize:11,color:T.text3 }}>{creatingPipeline ? "Creating default stages and opening settings..." : "Start with default stages and customize as needed"}</div>
              </button>
            </div>
            <button disabled={creatingPipeline} onClick={() => setNewPipelineModal(false)} style={{ marginTop:16,width:"100%",padding:"9px",borderRadius:10,border:`1px solid ${T.border1}`,background:"transparent",color:T.text3,cursor:creatingPipeline?"not-allowed":"pointer",opacity:creatingPipeline?0.55:1,fontFamily:base.font,fontSize:13 }}>Cancel</button>
          </div>
        </div>
      )}
      {/* Delete pipeline confirm */}
      {deletingPipeline && (
        <div style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(5px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
          <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:20,padding:"28px",width:"100%",maxWidth:360,boxShadow:"0 24px 70px rgba(0,0,0,0.6)" }}>
            <div style={{ fontSize:15,fontWeight:800,color:"#f43f5e",marginBottom:8 }}>Pipeline Delete Karo?</div>
            <div style={{ fontSize:13,color:T.text2,marginBottom:20,lineHeight:1.5 }}>
              <strong>"{pipelines.find(p=>p._id===deletingPipeline)?.name}"</strong> delete ho jaegi. Is pipeline k contacts default pipeline mein move ho jaenge.
            </div>
            <div style={{ display:"flex",gap:10 }}>
              <button onClick={() => setDeletingPipeline(null)} style={{ flex:1,padding:"10px",borderRadius:10,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:13,fontWeight:600 }}>Cancel</button>
              <button onClick={() => deletePipeline(deletingPipeline)} style={{ flex:1,padding:"10px",borderRadius:10,border:"none",background:"#f43f5e",color:"#fff",cursor:"pointer",fontFamily:base.font,fontSize:13,fontWeight:700 }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pipeline Sidebar ────────────────────────────────────────── */}
      <div style={{
        width: isTablet ? "100%" : 220,
        flexShrink: 0,
        borderRight: isTablet ? "none" : `1px solid ${T.border1}`,
        borderBottom: isTablet ? `1px solid ${T.border1}` : "none",
        display:"flex",
        flexDirection:"column",
        background:T.bg1,
        height: isTablet ? "auto" : "100%",
        maxHeight: isTablet ? 240 : "none",
        overflow:"hidden",
      }}>
        <div style={{ padding:"14px 14px 10px",borderBottom:`1px solid ${T.border1}`,flexShrink:0 }}>
          <div style={{ fontSize:10,fontWeight:700,color:T.text3,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10 }}>Lead Pipelines</div>
          {canManagePipelines && <button
            onClick={() => setNewPipelineModal(true)}
            style={{ width:"100%",padding:"8px 10px",borderRadius:9,border:"1px dashed rgba(99,102,241,0.4)",background:"rgba(99,102,241,0.06)",color:"#818cf8",cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6 }}
            onMouseOver={e=>{e.currentTarget.style.background="rgba(99,102,241,0.12)";}}
            onMouseOut={e=>{e.currentTarget.style.background="rgba(99,102,241,0.06)";}}
          >
            <span style={{ fontSize:16,lineHeight:1 }}>+</span> New Pipeline
          </button>}
        </div>
        <div style={{
          flex:1,
          overflowY: isTablet ? "auto" : "auto",
          padding:"8px 8px",
          display: isPhone ? "flex" : "block",
          gap: isPhone ? 8 : 0,
          overflowX: isPhone ? "auto" : "hidden",
        }}>
          {pipelines.map(pl => (
            <div key={pl._id} style={{ marginBottom:2, flex: isPhone ? "0 0 210px" : "initial" }}>
              {renamingId === pl._id ? (
                <div style={{ display:"flex",gap:4,padding:"4px 6px" }}>
                  <input
                    autoFocus
                    value={renameVal}
                    onChange={e=>setRenameVal(e.target.value)}
                    onKeyDown={e=>{if(e.key==="Enter")renamePipeline(pl._id);if(e.key==="Escape")setRenamingId(null);}}
                    style={{ flex:1,padding:"5px 8px",borderRadius:7,border:`1px solid ${T.emerald}`,background:T.bg0,color:T.text1,fontSize:12,outline:"none",fontFamily:base.font }}
                  />
                  <button onClick={()=>renamePipeline(pl._id)} style={{ padding:"5px 8px",borderRadius:7,border:"none",background:T.emerald,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700 }}>✓</button>
                </div>
              ) : (
                <div
                  onClick={() => switchPipeline(pl._id)}
                  style={{
                    display:"flex",alignItems:"center",gap:6,padding:"9px 10px",borderRadius:9,cursor:"pointer",
                    background: activePipelineId===pl._id ? "rgba(99,102,241,0.12)" : "transparent",
                    border: activePipelineId===pl._id ? "1px solid rgba(99,102,241,0.3)" : "1px solid transparent",
                    transition:"all 0.12s",
                  }}
                  onMouseOver={e=>{if(activePipelineId!==pl._id)e.currentTarget.style.background="rgba(255,255,255,0.04)";}}
                  onMouseOut={e=>{if(activePipelineId!==pl._id)e.currentTarget.style.background="transparent";}}
                >
                  <div style={{ width:7,height:7,borderRadius:"50%",flexShrink:0,background: activePipelineId===pl._id ? "#6366f1" : T.text3,opacity: activePipelineId===pl._id ? 1 : 0.4 }} />
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:12,fontWeight:activePipelineId===pl._id?700:500,color:activePipelineId===pl._id?T.text1:T.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{pl.name}</div>
                    <div style={{ fontSize:10,color:T.text3,marginTop:1 }}>{pl.contactCount||0} leads</div>
                  </div>
                  {pl.isDefault && <span style={{ fontSize:9,padding:"1px 5px",borderRadius:4,background:"rgba(99,102,241,0.15)",color:"#818cf8",flexShrink:0 }}>default</span>}
                  {canManagePipelines && <div style={{ display:"flex",gap:2,flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                    <button title="Rename" onClick={()=>{setRenamingId(pl._id);setRenameVal(pl.name);}} style={{ padding:"2px 5px",borderRadius:5,border:"none",background:"transparent",color:T.text3,cursor:"pointer",fontSize:11,opacity:0.6 }}>✏</button>
                    {pipelines.length>1 && <button title="Delete" onClick={()=>setDeletingPipeline(pl._id)} style={{ padding:"2px 5px",borderRadius:5,border:"none",background:"transparent",color:"#f43f5e",cursor:"pointer",fontSize:11,opacity:0.6 }}>🗑</button>}
                  </div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Board Area ─────────────────────────────────────────── */}
      <div style={{
        flex:1,
        display:"flex",
        flexDirection:"column",
        overflow:"hidden",
        minWidth: 0,
        minHeight: isTablet ? 620 : 0,
      }}>

      {/* Stats bar */}
      <div style={{ padding: isPhone ? "12px 12px 0" : "14px 20px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: isPhone ? 8 : 10, marginBottom: 14, flexWrap: "wrap" }}>
          <StatCard label="Active Leads"    value={stats.total}      color={T.sky}     />
          <StatCard label="Interested"      value={stats.interested} color={T.amber}   />
          <StatCard label="Proposals Out"   value={stats.proposals}  color="#8b5cf6"   />
          <StatCard label="Won Clients"     value={stats.clients}    color={T.emerald} />
          <StatCard label="Conversion Rate" value={`${stats.convRate}%`} color={T.emerald} sub="won / total" />

          <div style={{
            marginLeft: isTablet ? 0 : "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            width: isTablet ? "100%" : "auto",
          }}>
            <button
              onClick={toggleGlobalAi}
              disabled={globalAiSaving}
              title={globalAiEnabled ? "AI can reply across all Lead Gen chats" : "AI is paused across all Lead Gen chats"}
              style={{
                padding: "8px 13px", borderRadius: 10, cursor: globalAiSaving ? "wait" : "pointer",
                background: globalAiEnabled ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)",
                border: `1px solid ${globalAiEnabled ? "rgba(16,185,129,0.35)" : "rgba(245,158,11,0.4)"}`,
                color: globalAiEnabled ? T.emerald : T.amber,
                fontSize: 12, fontFamily: base.font, fontWeight: 800,
                flex: isPhone ? "1 1 100%" : "0 0 auto",
              }}
            >
              Customer AI: {globalAiEnabled ? "ON" : "OFF"}
            </button>
            <div style={{ position: "relative", flex: isPhone ? "1 1 100%" : "0 0 auto" }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search pipeline..."
                style={{ width: isPhone ? "100%" : 190, padding: "8px 11px 8px 32px", background: T.bg1, border: `1px solid ${T.border1}`, borderRadius: 10, color: T.text1, fontSize: 12, outline: "none", fontFamily: base.font }}
                onFocus={e => (e.target.style.borderColor = T.emerald)}
                onBlur={e  => (e.target.style.borderColor = T.border1)}
              />
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, opacity: 0.4 }}>🔍</span>
            </div>
            <button
              onClick={() => setAiGenOpen(true)}
              style={{
                padding: "8px 14px", background: "linear-gradient(135deg,rgba(99,102,241,0.15),rgba(139,92,246,0.15))",
                border: "1px solid rgba(99,102,241,0.4)",
                borderRadius: 10, color: "#a78bfa", fontSize: 12, cursor: "pointer", fontFamily: base.font, fontWeight: 700,
                flex: isPhone ? "1 1 0" : "0 0 auto",
              }}
            >
              ✨ AI Setup
            </button>
            <button
              onClick={() => setSettingsOpen(true)}
              style={{
                padding: "8px 14px", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)",
                borderRadius: 10, color: "#818cf8", fontSize: 12, cursor: "pointer", fontFamily: base.font, fontWeight: 600,
                flex: isPhone ? "1 1 0" : "0 0 auto",
              }}
            >
              ⚙ Customize
            </button>
            <button onClick={load} style={{ padding: "8px 14px", background: T.bg1, border: `1px solid ${T.border1}`, borderRadius: 10, color: T.text2, fontSize: 12, cursor: "pointer", fontFamily: base.font, flex: isPhone ? "1 1 0" : "0 0 auto" }}>
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: "flex", flexDirection: isTablet ? "column" : "row", overflow: "hidden", minWidth: 0 }}>

        {/* Kanban board */}
        <div style={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          padding: isPhone ? "0 12px 16px" : "0 20px 20px",
          gap: isPhone ? 10 : 12,
          minWidth: 0,
          minHeight: isPhone ? 430 : 0,
        }}>
          {loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 32, height: 32, border: `2px solid ${T.border1}`, borderTopColor: T.emerald, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            </div>
          ) : (
            boardStages.map(stage => {
              const stageCards = cardsForStage(stage);
              const cards  = filterCards(stageCards);
              const isOver = dragOver === stage.id;
              return (
                <div
                  key={stage.id}
                  onDragOver={stage.isHandoff ? undefined : e => onDragOver(e, stage.id)}
                  onDrop={stage.isHandoff ? undefined : e => onDrop(e, stage.id)}
                  style={{
                    width: isPhone ? "min(82vw, 300px)" : 230, flexShrink: 0, display: "flex", flexDirection: "column",
                    background: isOver ? `${stage.color}08` : T.bg1,
                    border: `1px solid ${isOver ? stage.color + "44" : T.border1}`,
                    borderRadius: 14, overflow: "hidden",
                    transition: "all 0.15s", boxShadow: isOver ? `0 0 0 2px ${stage.color}22` : "none",
                  }}
                >
                  {/* Column header */}
                  <div style={{ padding: "12px 14px 10px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0, background: `${stage.color}09` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: stage.color, display: "inline-block", flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: stage.color }}>{stage.label}</span>
                        {stage.isWon  && <span style={{ fontSize: 8, color: "#10b981", fontWeight: 800, letterSpacing: "0.05em" }}>WON</span>}
                        {stage.isLost && <span style={{ fontSize: 8, color: "#f43f5e", fontWeight: 800, letterSpacing: "0.05em" }}>LOST</span>}
                      </div>
                      <div style={{
                        minWidth: 20, height: 20, borderRadius: 10,
                        background: `${stage.color}20`, color: stage.color,
                        fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
                      }}>
                        {stageCards.length}
                      </div>
                    </div>
                    <div style={{ height: 2, borderRadius: 1, background: `linear-gradient(90deg, ${stage.color}, ${stage.color}44)`, marginTop: 8 }} />
                  </div>

                  {/* Cards */}
                  <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
                    {cards.length === 0 ? (
                      <div style={{ padding: "20px 8px", textAlign: "center", color: T.text3, fontSize: 11 }}>
                        {search ? "No matches" : stage.isHandoff ? "AI-paused leads appear here automatically" : "Drop here"}
                      </div>
                    ) : (
                      cards.map(card => (
                        <PipelineCard
                          key={card.contact}
                          card={card}
                          stage={stage}
                          isSelected={selected?.contact === card.contact}
                          onClick={() => openContact(card.contact, stage.id)}
                          onDragStart={() => onDragStart(card.contact, card.pipelineStage)}
                          onDragEnd={onDragEnd}
                          isDragging={dragging?.contact === card.contact}
                          stageList={stageConfig}
                          onMove={(to) => moveToStage(card.contact, card.pipelineStage, to)}
                          canDrag={!stage.isHandoff}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right slide panel */}
        {selected && (
          <div style={{
            width: isTablet ? "100%" : 380,
            height: isTablet ? (isPhone ? 520 : 560) : "auto",
            flexShrink: 0,
            borderLeft: isTablet ? "none" : `1px solid ${T.border1}`,
            borderTop: isTablet ? `1px solid ${T.border1}` : "none",
            background: T.bg1, display: "flex", flexDirection: "column", overflow: "hidden",
            animation: "slideRight 0.2s ease", position: "relative",
          }}>
            <style>{`@keyframes slideRight{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}`}</style>

            {/* Panel header */}
            <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar
                  name={selectedCard?.name || selectedCard?.phone || selected.contact}
                  size={40}
                  color={stageMap[selected.stage]?.color || T.emerald}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedCard?.name || selectedCard?.phone || cleanPhone(selected.contact)}
                  </div>
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                    {selectedCard?.phone && <span>{selectedCard.phone}</span>}
                    {chatMeta.isHumanMode && (
                      <span style={{ padding: "1px 6px", borderRadius: 8, background: "rgba(245,158,11,0.12)", color: T.amber, fontSize: 9, fontWeight: 700 }}>AI OFF</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => setConfirmDel(true)} title="Delete contact"
                    style={{ background: "none", border: "none", color: "#f43f5e", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 4, opacity: 0.7 }}>
                    🗑
                  </button>
                  <button onClick={() => { setSelected(null); setConfirmDel(false); }}
                    style={{ background: "none", border: "none", color: T.text3, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
                </div>
              </div>

              <div style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 12,
                background: T.bg2,
                border: `1px solid ${T.border1}`,
                display: "grid",
                gridTemplateColumns: isPhone ? "1fr" : "1fr auto",
                gap: 10,
                alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: T.text1 }}>Customer AI</div>
                  <div style={{ fontSize: 10, color: T.text3, marginTop: 2 }}>
                    {!globalAiEnabled
                      ? "Globally off: all customers are manual"
                      : chatMeta.isHumanMode
                      ? "Off: agent will handle this customer manually"
                      : "On: AI can reply if agent does not answer first"}
                  </div>
                </div>
                <button
                  onClick={() => updateAiSettings({ isHumanMode: !chatMeta.isHumanMode })}
                  disabled={!globalAiEnabled}
                  style={{
                    padding: "7px 11px", borderRadius: 10, cursor: !globalAiEnabled ? "not-allowed" : "pointer", fontFamily: base.font, fontSize: 11, fontWeight: 800,
                    border: `1px solid ${chatMeta.isHumanMode ? "rgba(245,158,11,0.45)" : "rgba(16,185,129,0.35)"}`,
                    background: chatMeta.isHumanMode ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.12)",
                    color: chatMeta.isHumanMode ? T.amber : T.emerald,
                    opacity: globalAiEnabled ? 1 : 0.55,
                    width: isPhone ? "100%" : "auto",
                  }}
                >
                  {chatMeta.isHumanMode ? "OFF" : "ON"}
                </button>
              </div>

            </div>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
              {[{ id: "chat", label: "Chat" }, { id: "comments", label: "Comments" }, { id: "profile", label: "Profile" }].map(t => (
                <div key={t.id} onClick={() => setPanelTab(t.id)}
                  style={{
                    flex: 1, padding: "10px 0", textAlign: "center", cursor: "pointer", fontSize: 12, fontWeight: 600,
                    color: panelTab === t.id ? T.emerald : T.text3,
                    borderBottom: panelTab === t.id ? `2px solid ${T.emerald}` : "2px solid transparent",
                    marginBottom: -1, transition: "all 0.15s",
                  }}>
                  {t.label}
                </div>
              ))}
            </div>

            {/* Delete confirmation overlay */}
            {confirmDel && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 20,
                background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
              }}>
                <div style={{ background: T.bg1, border: "1px solid rgba(244,63,94,0.3)", borderRadius: 16, padding: 24, width: "100%", maxWidth: 300 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#f43f5e", marginBottom: 8 }}>Delete Contact?</div>
                  <div style={{ fontSize: 13, color: T.text2, marginBottom: 18, lineHeight: 1.5 }}>
                    This will permanently delete <strong style={{ color: T.text1 }}>
                      {selectedCard?.name || cleanPhone(selected?.contact)}
                    </strong> and their entire chat history.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={deleteContact} disabled={deleting}
                      style={{ flex: 1, padding: "9px", borderRadius: 9, background: "#f43f5e", color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: base.font }}>
                      {deleting ? "Deleting…" : "Yes, Delete"}
                    </button>
                    <button onClick={() => setConfirmDel(false)}
                      style={{ flex: 1, padding: "9px", borderRadius: 9, background: T.bg2, color: T.text2, border: `1px solid ${T.border1}`, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab content */}
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

              {/* CHAT TAB */}
              {panelTab === "chat" && (
                <>
                  <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {chatLoading ? (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ width: 24, height: 24, border: `2px solid ${T.border1}`, borderTopColor: T.emerald, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                      </div>
                    ) : chat.length === 0 ? (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.text3, fontSize: 12 }}>
                        No messages yet
                      </div>
                    ) : (
                      chat.map((m, i) => (
                        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-start" : "flex-end" }}>
                          <div style={{
                            maxWidth: "85%", padding: "8px 12px", wordBreak: "break-word",
                            borderRadius: m.role === "user" ? "14px 14px 14px 3px" : "14px 14px 3px 14px",
                            background: m.role === "user" ? T.bg2 : `linear-gradient(135deg,rgba(99,102,241,0.16),rgba(99,102,241,0.07))`,
                            border: `1px solid ${m.role === "user" ? T.border1 : "rgba(99,102,241,0.2)"}`,
                            color: T.text1, fontSize: 12, lineHeight: 1.6,
                          }}>
                            {m.content}
                          </div>
                          <div style={{ fontSize: 10, color: T.text3, marginTop: 2, padding: "0 3px" }}>
                            {m.role === "user" ? "📱" : "🤖"}{" "}
                            {m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.border1}`, flexShrink: 0 }}>
                    {chatMeta.isHumanMode && (
                      <div style={{ fontSize: 11, color: T.amber, textAlign: "center", padding: "6px 0" }}>
                        🤖 AI auto-reply is OFF — you are handling this client directly
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                        placeholder="Type a message… (Enter to send)"
                        rows={2}
                        style={{
                          flex: 1, padding: "8px 11px", background: T.bg0, border: `1px solid ${T.border1}`,
                          borderRadius: 10, color: T.text1, fontSize: 12, outline: "none", resize: "none",
                          fontFamily: base.font, lineHeight: 1.5,
                        }}
                        onFocus={e => (e.target.style.borderColor = T.emerald)}
                        onBlur={e  => (e.target.style.borderColor = T.border1)}
                      />
                      <button onClick={sendMsg} disabled={sending || !message.trim()}
                        style={{
                          padding: "0 14px", borderRadius: 10, cursor: sending || !message.trim() ? "default" : "pointer",
                          background: sending || !message.trim() ? T.bg2 : "linear-gradient(135deg,#6366f1,#4f46e5)",
                          border: "none", color: sending || !message.trim() ? T.text3 : "#fff",
                          fontSize: 16, transition: "all 0.15s", flexShrink: 0,
                        }}>
                        {sending ? "…" : "➤"}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* COMMENTS TAB */}
              {panelTab === "comments" && (
                <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                  <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {(profile?.comments || []).length === 0 ? (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.text3, fontSize: 12, textAlign: "center", lineHeight: 1.6 }}>
                        No internal comments yet
                      </div>
                    ) : (
                      [...(profile.comments || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((c, i) => (
                        <div key={c._id || i} style={{ padding: "10px 11px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 5 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: T.text1 }}>{c.authorName || "Team"}</div>
                            <div style={{ fontSize: 10, color: T.text3 }}>{c.createdAt ? new Date(c.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                          </div>
                          <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{c.text}</div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.border1}`, flexShrink: 0 }}>
                    <textarea
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Add internal comment for owner/agent..."
                      rows={3}
                      style={{ width: "100%", boxSizing: "border-box", padding: "9px 11px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, color: T.text1, fontSize: 12, outline: "none", resize: "none", fontFamily: base.font, lineHeight: 1.5 }}
                    />
                    <button onClick={addComment} disabled={commentSaving || !commentText.trim()}
                      style={{ marginTop: 8, width: "100%", padding: "9px", borderRadius: 10, border: "none", cursor: commentSaving || !commentText.trim() ? "default" : "pointer", background: commentSaving || !commentText.trim() ? T.bg2 : "linear-gradient(135deg,#6366f1,#4f46e5)", color: commentSaving || !commentText.trim() ? T.text3 : "#fff", fontFamily: base.font, fontSize: 12, fontWeight: 800 }}>
                      {commentSaving ? "Saving..." : "Add Comment"}
                    </button>
                  </div>
                </div>
              )}

              {/* PROFILE TAB */}
              {panelTab === "profile" && (
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px" }}>
                  {!profile ? (
                    <div style={{ color: T.text3, fontSize: 12, textAlign: "center", marginTop: 40 }}>No profile data</div>
                  ) : (
                    <>
                      <ProfileRow label="Phone"         value={profile.phone || cleanPhone(selected.contact)} />
                      <ProfileRow label="Industry"      value={profile.industry} />
                      <ProfileRow label="Interested In" value={profile.interestedIn} />
                      <ProfileRow label="Intention"     value={profile.intention} />
                      <ProfileRow label="Notes"         value={profile.notes} />
                      <ProfileRow label="Last Contact"  value={profile.lastContactedAt ? new Date(profile.lastContactedAt).toLocaleDateString() : "—"} />
                      {(profile.tags || []).length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Tags</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                            {profile.tags.map(t => (
                              <span key={t} style={{ padding: "2px 8px", borderRadius: 20, background: "rgba(139,92,246,0.12)", color: "#8b5cf6", fontSize: 10, fontWeight: 600 }}>{t}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Deal Value</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.emerald }}>
                          {profile.dealValue ? `$${profile.dealValue.toLocaleString()}` : "—"}
                        </div>
                      </div>

                      {wonStage && selected.stage !== wonStage.id && (
                        <button onClick={() => moveToStage(selected.contact, selected.stage, wonStage.id)} disabled={moving}
                          style={{
                            width: "100%", padding: "11px", borderRadius: 12, cursor: moving ? "default" : "pointer",
                            background: "linear-gradient(135deg,#6366f1,#4f46e5)", border: "none",
                            color: "#fff", fontWeight: 700, fontSize: 13, fontFamily: base.font,
                            boxShadow: "0 4px 16px rgba(99,102,241,0.3)", transition: "all 0.15s",
                          }}>
                          ✅ Mark as {wonStage.label}
                        </button>
                      )}
                      {wonStage && selected.stage === wonStage.id && (
                        <div style={{ padding: "11px", borderRadius: 12, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", textAlign: "center", color: T.emerald, fontSize: 13, fontWeight: 700 }}>
                          ✅ {wonStage.label} — AI is OFF
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>{/* end Main Board Area */}
    </div>
  );
}
